import { useMemo } from 'react';
import { useNotifications } from './useNotifications';
import { mapLegacyType } from '../components/DynamicIsland';
import type { DynamicIslandNotification } from '../components/DynamicIsland';

/**
 * Hook to convert legacy notifications to Dynamic Island format
 * Bridges the gap between existing notification system and new Dynamic Island
 */
export function useDynamicIslandNotifications() {
  const {
    notifications,
    isLoading,
    error,
    unreadCount,
    markAsRead,
    markAllAsRead,
    dismissNotification,
  } = useNotifications();

  // Convert legacy notifications to Dynamic Island format
  const dynamicIslandNotifications = useMemo<DynamicIslandNotification[]>(() => {
    return notifications.map((notification) => {
      // Extract title from message (first line or first 50 chars)
      const messageLines = notification.message.split('\n');
      const title = messageLines[0].length > 50
        ? messageLines[0].substring(0, 50) + '...'
        : messageLines[0];
      const message = messageLines.length > 1
        ? messageLines.slice(1).join('\n')
        : notification.message;

      // Determine priority based on notification type
      let priority: 'urgent' | 'normal' | 'info' = 'normal';
      if (notification.type === 'error') priority = 'urgent';
      if (notification.type === 'warning') priority = 'urgent';
      if (notification.type === 'info') priority = 'info';

      // Ensure createdAt is a Date object (might be string from localStorage)
      const timestamp = notification.createdAt instanceof Date
        ? notification.createdAt
        : new Date(notification.createdAt);

      return {
        id: notification.id,
        type: mapLegacyType(notification.type),
        title,
        message,
        timestamp,
        isRead: notification.isRead,
        priority,
        actionUrl: undefined, // Can be enhanced later
        actionLabel: undefined,
        avatar: undefined,
        metadata: {
          legacyType: notification.type,
        },
      };
    });
  }, [notifications]);

  // Get unread notifications only
  const unreadDynamicIslandNotifications = useMemo(
    () => dynamicIslandNotifications.filter(n => !n.isRead),
    [dynamicIslandNotifications]
  );

  // Handle notification click - mark as read
  const handleNotificationClick = async (id: string) => {
    await markAsRead(id);
  };

  // Handle notification dismiss - mark as read and hide
  const handleNotificationDismiss = async (id: string) => {
    await dismissNotification(id);
  };

  return {
    notifications: dynamicIslandNotifications,
    unreadNotifications: unreadDynamicIslandNotifications,
    isLoading,
    error,
    unreadCount,
    handleNotificationClick,
    handleNotificationDismiss,
    markAllAsRead,
  };
}
